import React, { useState, useEffect } from 'react';
import { Input } from '@/components/ui/Input/Input';
import type { RoomBreakdownProps, VenueRoomBreakdown } from './types';
import { CURRENCY_INFO, type SupportedCurrency } from './types';

export function RoomBreakdown({
  shortlistId,
  breakdown,
  nights,
  displayCurrency,
  onChange,
}: RoomBreakdownProps) {
  const currency = displayCurrency as SupportedCurrency;
  const currencyInfo = CURRENCY_INFO[currency] ?? CURRENCY_INFO.USD;

  // Helper to format currency value from cents to dollars with 2 decimal places
  const formatCurrencyValue = (cents: number | null): string => {
    if (cents === null) return '';
    return (cents / 100).toFixed(2);
  };

  // Track which field is currently being edited to prevent interruption
  const [editingField, setEditingField] = useState<string | null>(null);

  // Local state for controlled inputs
  const [localValues, setLocalValues] = useState({
    single_rooms: breakdown.single_rooms?.toString() ?? '0',
    single_room_rate: formatCurrencyValue(breakdown.single_room_rate),
    double_rooms: breakdown.double_rooms?.toString() ?? '0',
    double_room_rate: formatCurrencyValue(breakdown.double_room_rate),
    room_nights: breakdown.room_nights?.toString() ?? nights.toString(),
  });

  // Sync local values when breakdown prop changes (but not for fields currently being edited)
  useEffect(() => {
    setLocalValues(prev => ({
      single_rooms: editingField === 'single_rooms' ? prev.single_rooms : (breakdown.single_rooms?.toString() ?? '0'),
      single_room_rate: editingField === 'single_room_rate' ? prev.single_room_rate : formatCurrencyValue(breakdown.single_room_rate),
      double_rooms: editingField === 'double_rooms' ? prev.double_rooms : (breakdown.double_rooms?.toString() ?? '0'),
      double_room_rate: editingField === 'double_room_rate' ? prev.double_room_rate : formatCurrencyValue(breakdown.double_room_rate),
      room_nights: editingField === 'room_nights' ? prev.room_nights : (breakdown.room_nights?.toString() ?? nights.toString()),
    }));
  }, [breakdown, nights, editingField]);

  const handleChange = (field: keyof typeof localValues, value: string) => {
    // Preserve user input as-is while typing
    setLocalValues(prev => ({ ...prev, [field]: value }));

    // Convert to DB format and notify parent
    const numValue = parseFloat(value) || 0;
    let dbValue: number | null = null;

    if (field === 'single_room_rate' || field === 'double_room_rate') {
      // Convert dollars to cents
      dbValue = value ? Math.round(numValue * 100) : null;
    } else {
      dbValue = Math.round(numValue);
    }

    onChange({ [field]: dbValue });
  };

  // Handle focus to track editing state
  const handleFocus = (field: keyof typeof localValues) => {
    setEditingField(field);
  };

  // Format currency values on blur to ensure 2 decimal places
  const handleBlur = (field: keyof typeof localValues) => {
    setEditingField(null);
    if (field === 'single_room_rate' || field === 'double_room_rate') {
      const numValue = parseFloat(localValues[field]);
      if (!isNaN(numValue)) {
        setLocalValues(prev => ({ ...prev, [field]: numValue.toFixed(2) }));
      }
    }
  };

  // Calculate totals for display
  const roomNights = parseInt(localValues.room_nights) || nights;
  const sglRate = parseFloat(localValues.single_room_rate) || 0;
  const dblRate = parseFloat(localValues.double_room_rate) || 0;
  const sglCount = parseInt(localValues.single_rooms) || 0;
  const dblCount = parseInt(localValues.double_rooms) || 0;

  const sglTotal = sglRate * sglCount * roomNights;
  const dblTotal = dblRate * dblCount * roomNights;
  const grandTotal = sglTotal + dblTotal;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <div className="space-y-4">
      {/* Nights Override */}
      <div className="flex items-center gap-4 mb-4">
        <label className="text-sm font-medium text-gray-700 w-32">Room Nights:</label>
        <Input
          type="number"
          min="1"
          value={localValues.room_nights}
          onChange={(e) => handleChange('room_nights', e.target.value)}
          className="w-24"
        />
        <span className="text-sm text-gray-500">(defaults to event nights: {nights})</span>
      </div>

      {/* Room Breakdown Table */}
      <table className="w-full text-sm table-fixed">
          <colgroup>
            <col className="w-auto" />
            <col className="w-32" />
            <col className="w-40" />
            <col className="w-32" />
          </colgroup>
          <thead>
            <tr className="text-left border-b border-amber-200">
              <th className="py-2 px-3 font-medium text-amber-900">Room Type</th>
              <th className="py-2 px-3 font-medium text-amber-900 text-center">Count</th>
              <th className="py-2 px-3 font-medium text-amber-900 text-right">Rate/Night ({currencyInfo.symbol})</th>
              <th className="py-2 px-3 font-medium text-amber-900 text-right w-32">Total</th>
            </tr>
          </thead>
          <tbody>
            {/* Single Rooms */}
            <tr className="border-b border-amber-100">
              <td className="py-3 px-3 font-medium text-gray-900">Single (SGL)</td>
              <td className="py-3 px-3">
                <div className="flex justify-center">
                  <div className="w-24">
                    <Input
                      type="number"
                      min="0"
                      value={localValues.single_rooms}
                      onChange={(e) => handleChange('single_rooms', e.target.value)}
                      className="text-center"
                    />
                  </div>
                </div>
              </td>
              <td className="py-3 px-3 text-right">
                <div className="flex justify-end">
                  <div className="w-36">
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                      value={localValues.single_room_rate}
                      onChange={(e) => handleChange('single_room_rate', e.target.value)}
                      onFocus={() => handleFocus('single_room_rate')}
                      onBlur={() => handleBlur('single_room_rate')}
                      className="text-right"
                    />
                  </div>
                </div>
              </td>
              <td className="py-3 px-3 text-right font-medium text-gray-900 w-32">
                <span className="inline-block min-w-[120px] text-right">{formatCurrency(sglTotal)}</span>
              </td>
            </tr>

            {/* Double Rooms */}
            <tr className="border-b border-amber-100">
              <td className="py-3 px-3 font-medium text-gray-900">Double (DBL)</td>
              <td className="py-3 px-3">
                <div className="flex justify-center">
                  <div className="w-24">
                    <Input
                      type="number"
                      min="0"
                      value={localValues.double_rooms}
                      onChange={(e) => handleChange('double_rooms', e.target.value)}
                      className="text-center"
                    />
                  </div>
                </div>
              </td>
              <td className="py-3 px-3 text-right">
                <div className="flex justify-end">
                  <div className="w-36">
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                      value={localValues.double_room_rate}
                      onChange={(e) => handleChange('double_room_rate', e.target.value)}
                      onFocus={() => handleFocus('double_room_rate')}
                      onBlur={() => handleBlur('double_room_rate')}
                      className="text-right"
                    />
                  </div>
                </div>
              </td>
              <td className="py-3 px-3 text-right font-medium text-gray-900 w-32">
                <span className="inline-block min-w-[120px] text-right">{formatCurrency(dblTotal)}</span>
              </td>
            </tr>

            {/* Total Row */}
            <tr className="bg-amber-100">
              <td className="py-3 px-3 font-bold text-amber-900" colSpan={3}>
                Total Accommodation ({sglCount + dblCount} rooms × {roomNights} nights)
              </td>
              <td className="py-3 px-3 text-right font-bold text-amber-900 w-32">
                <span className="inline-block min-w-[120px] text-right">{formatCurrency(grandTotal)}</span>
              </td>
            </tr>
          </tbody>
        </table>
    </div>
  );
}

export default RoomBreakdown;
